import { useEffect, useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Checkbox } from '../../../../components/inputs/Checkbox';
import { DateField } from '../../../../components/inputs/DateField';
import { Select, type SelectOption } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { ppmpsApi } from '../ppmpsApi';
import type { PpmpDetail, PpmpPpaCandidate } from '../ppmpTypes';
import type { AnnualProcurementPlanDetail, AppDepartmentRow } from '../procurementPlanTypes';
import { MAX_GENERATE_PROBES, milestoneDates, shuffled } from '../utils/samplePpmp';

/**
 * File a PPMP, or correct one.
 *
 * **The record is filed EMPTY.** Its procurement lines are a sub-resource added from the
 * profile, exactly as a PPA's budgeted lines are — a department builds them up over days, so a
 * correction to the title must not flag every line away and write fresh ones.
 *
 * **Three rungs, each drawn always and DISABLED until the one above is answered**: the
 * department, the PPA it carries out, and the budgeted line it procures against. A required
 * field that appears out of nowhere moves every field under it while the reader works, which
 * is why the container-then-member pair is two pickers in one step rather than one that grows.
 */
export function PpmpModal({
  open,
  plan,
  department,
  departments,
  editing,
  onClose,
  onSaved,
}: {
  open: boolean;
  plan: AnnualProcurementPlanDetail;
  /** Fixed where a roster row opened the form; null where the toolbar did. */
  department: AppDepartmentRow | null;
  /** The roster, for the department picker. Unused where one is already fixed. */
  departments?: AppDepartmentRow[];
  editing: PpmpDetail | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [orgUnitId, setOrgUnitId] = useState(
    editing?.orgUnitId ?? department?.orgUnitId ?? '',
  );
  const [title, setTitle] = useState(editing?.title ?? '');
  const [ppaId, setPpaId] = useState(editing?.ppaId ?? '');
  /*
    NOT A FIELD. The column predates this form and the API still carries it, so an edit sends
    back whatever was already stored rather than silently clearing it — but nothing asks for it,
    because the form collects what the reader was asked to decide and this was never that.

    It cannot drift on an edit: the PPA is honoured on a CREATE and ignored on an update, so the
    stored line always belongs to the PPA it was filed under.
  */
  const ppaLineItemId = editing?.ppaLineItemId ?? '';
  const [earlyProcurement, setEarlyProcurement] = useState(
    editing?.coveredByEarlyProcurement ?? false,
  );
  const [postingDate, setPostingDate] = useState(editing?.postingDate ?? '');
  const [openingOfBidsDate, setOpeningOfBidsDate] = useState(editing?.openingOfBidsDate ?? '');
  const [noticeOfAwardDate, setNoticeOfAwardDate] = useState(editing?.noticeOfAwardDate ?? '');
  const [contractSigningDate, setContractSigningDate] = useState(
    editing?.contractSigningDate ?? '',
  );

  const [ppas, setPpas] = useState<PpmpPpaCandidate[]>([]);
  const [loadingPpas, setLoadingPpas] = useState(false);

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  // The department is FIXED on an edit and wherever a roster row opened the form. Moving a
  // plan to a different office is a withdrawal and a fresh filing, not a correction.
  const departmentFixed = editing !== null || department !== null;
  const departmentName = editing?.orgUnitName ?? department?.orgUnitName ?? null;

  /*
    The PPA feed is scoped to the DEPARTMENT, so it refetches whenever that answer changes —
    and the two rungs below it are cleared with it, or the form would post a PPA belonging to
    an office the reader has just moved off.
  */
  useEffect(() => {
    if (!orgUnitId) {
      setPpas([]);
      return;
    }

    const controller = new AbortController();
    setLoadingPpas(true);

    ppmpsApi
      .ppaCandidates(plan.id, orgUnitId, controller.signal)
      .then((rows) => {
        if (!controller.signal.aborted) setPpas(rows);
      })
      .catch(() => {
        // Fails soft — the save still refuses a PPA of another department or another year.
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingPpas(false);
      });

    return () => controller.abort();
  }, [plan.id, orgUnitId]);

  function chooseDepartment(next: string) {
    setOrgUnitId(next);
    setPpaId('');
  }

  function choosePpa(next: string) {
    setPpaId(next);

    // The title DEFAULTS to the PPA's name and stays editable: two PPMPs under one PPA need
    // telling apart, so this is a convenience for the first of them rather than a copy the
    // record is obliged to keep in step.
    const chosen = ppas.find((p) => p.ppaId === next);

    if (chosen && !title.trim()) {
      setTitle(chosen.name);
    }
  }

  const departmentOptions: SelectOption[] = (departments ?? []).map((d) => ({
    value: d.orgUnitId,
    label: d.orgUnitName,
    sublabel: d.orgUnitCode ?? undefined,
    right: d.ppmpCount > 0 ? `${d.ppmpCount} filed` : undefined,
    keywords: d.orgUnitCode ?? '',
  }));

  const ppaOptions: SelectOption[] = ppas.map((p) => ({
    // An option SAYS WHAT IT IS — the NAME is what a reader recognises a PPA by, and the
    // number goes in the sublabel where it is still searchable through `keywords`.
    value: p.ppaId,
    label: p.name,
    sublabel: [p.ppaNumber, p.expenseClassLabel].filter(Boolean).join(' · '),
    keywords: [p.ppaNumber, p.expenseClassLabel].filter(Boolean).join(' '),
  }));

  const canSave =
    orgUnitId.length > 0 && ppaId.length > 0 && title.trim().length > 0 && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const request = {
      annualProcurementPlanId: plan.id,
      orgUnitId,
      title: title.trim(),
      ppaId,
      ppaLineItemId: ppaLineItemId || null,
      coveredByEarlyProcurement: earlyProcurement,
      postingDate: postingDate || null,
      openingOfBidsDate: openingOfBidsDate || null,
      noticeOfAwardDate: noticeOfAwardDate || null,
      contractSigningDate: contractSigningDate || null,
    };

    try {
      if (editing) await ppmpsApi.update(editing.id, request);
      else await ppmpsApi.create(request);

      onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not save the PPMP.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'ppmp' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? `Edit ${editing.ppmpNumber}` : 'File a PPMP'}
      size="2xl"
      id="ppmp-modal"
      autogenerate={{
        /*
          Fills every required field from the LIVE feeds — a real department and a real PPA of
          that department, never an invented id, which the server would refuse and which would
          read as a bug in the form.

          **It FETCHES the department's PPAs rather than reading the picker's own list**, because
          that list arrives from an effect a render later: picking a department and stopping
          there left the two rungs below it empty, so the primary action did not enable and the
          reader had to press the button a second time to find out. The contract is ONE click.

          The DEPARTMENT is left alone where it is already fixed: never a field the reader has
          already decided. The EARLY PROCUREMENT flag stays at its default, because it is the
          one control here that changes what the record claims.
        */
        onGenerate: async () => {
          /*
            A department is only worth generating if it HAS a PPA under this plan, and most of
            a roster does not: a unit files a procurement plan against what it proposed for the
            budget year, and plenty of offices proposed nothing. Picking one at random filled
            nothing at all on most clicks, so the shuffled roster is tried in turn until one
            answers — resolving against the LIVE feed rather than hoping.
          */
          const queue = departmentFixed
            ? [orgUnitId].filter(Boolean)
            : shuffled(departmentOptions.map((d) => d.value)).slice(0, MAX_GENERATE_PROBES);

          let unit = '';
          let rows: PpmpPpaCandidate[] = [];

          for (const candidate of queue) {
            // Fails soft, exactly as the picker's own feed does — a generator that threw would
            // leave the form half-filled with no way to tell why.
            const found = await ppmpsApi.ppaCandidates(plan.id, candidate).catch(() => []);

            if (found.length > 0) {
              unit = candidate;
              rows = found;
              break;
            }
          }

          /*
            Nothing on the roster can file against this plan. The DEPARTMENT is still chosen,
            so the form shows its own sentence saying why there is no PPA — which is a truthful
            empty form rather than a button that did nothing visible.
          */
          if (rows.length === 0) {
            if (!departmentFixed && queue.length > 0) chooseDepartment(queue[0]);
            return;
          }

          if (unit !== orgUnitId) chooseDepartment(unit);

          // A PPA that HAS budgeted lines is preferred, so the generated record exercises the
          // third rung too; the rest are still eligible, or a plan whose PPAs carry no lines
          // yet would generate nothing at all.
          const withLines = rows.filter((r) => r.lineItems.length > 0);
          const source = withLines.length > 0 ? withLines : rows;
          const chosen = source[Math.floor(Math.random() * source.length)];

          setPpaId(chosen.ppaId);
          setTitle(`${chosen.name} — procurement plan`);

          // The optional half too: a record carrying only its required fields produces a
          // profile of em dashes. The four dates are the bidding timeline, so they are spaced
          // IN ORDER inside the plan's own coverage window rather than scattered.
          const [first, second, third, fourth] = milestoneDates(
            plan.coverageStartDate,
            plan.coverageEndDate,
          );
          setPostingDate(first);
          setOpeningOfBidsDate(second);
          setNoticeOfAwardDate(third);
          setContractSigningDate(fourth);
        },
        disabled: saving || (!departmentFixed && departmentOptions.length === 0),
      }}
      confirm={{
        id: 'ppmp-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="ppmp-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="ppmp-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {/*
          The plan is STATED, never offered: a PPMP is only ever filed from inside one, and the
          server ignores a plan id on an update. A read-only FACT, so the Table Display View.
        */}
        <PreviewTable
          variant="table-display"
          rows={[
            { label: 'Annual procurement plan', value: plan.appNumber, mono: true },
            { label: 'Fiscal year', value: String(plan.fiscalYear), mono: true },
            ...(departmentFixed && departmentName
              ? [{ label: 'Department', value: departmentName }]
              : []),
            ...(editing ? [{ label: 'PPMP number', value: editing.ppmpNumber, mono: true }] : []),
          ]}
        />

        {!departmentFixed ? (
          <Select
            id="ppmp-org-unit-id"
            label="Department"
            required
            description="The office filing this plan. Only enrolled departments appear — the roster is built from that list."
            value={orgUnitId || null}
            onChange={chooseDepartment}
            options={departmentOptions}
            clearable={false}
            disabled={saving}
            error={error?.fieldError('orgUnitId')}
          />
        ) : null}

        <Select
          id="ppmp-ppa-id"
          label="PPA"
          required
          /*
            DRAWN ALWAYS and disabled until the department is answered, with the reason in its
            own description — a required field appearing out of nowhere moves every field under
            it while the reader works.
          */
          description={
            orgUnitId
              ? 'The programme, project or activity this procurement carries out. Only this department’s PPAs for this fiscal year are offered.'
              : 'Choose a department first — the PPAs belong to it.'
          }
          value={ppaId || null}
          onChange={choosePpa}
          options={ppaOptions}
          loading={loadingPpas}
          clearable={false}
          disabled={saving || !orgUnitId}
          error={error?.fieldError('ppaId')}
        />

        {orgUnitId && !loadingPpas && ppaOptions.length === 0 ? (
          <p className="text-xs text-slate-500">
            This department has no PPA under {plan.budgetPlanNumber}. A PPMP carries out a PPA
            the same office proposed for the same year, so one has to be filed on the budget
            plan first.
          </p>
        ) : null}

        <TextField
          id="ppmp-title"
          label="Title"
          required
          description="What this plan is called. It defaults to the PPA's name and is yours to change — two plans under one PPA need telling apart."
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          disabled={saving}
          error={error?.fieldError('title')}
        />

        <label
          htmlFor="ppmp-covered-by-early-procurement"
          className="flex items-start gap-2 text-sm text-slate-600"
        >
          <Checkbox
            id="ppmp-covered-by-early-procurement"
            name="ppmp-covered-by-early-procurement"
            checked={earlyProcurement}
            onChange={() => setEarlyProcurement((v) => !v)}
            disabled={saving}
          />
          <span>
            Covered by an Early Procurement Activity — the procurement is carried out ahead of
            the budget&rsquo;s approval.
          </span>
        </label>

        {/*
          The four milestones. ALL OPTIONAL and none defaulted — they are what a plan INTENDS,
          so a plan that has not scheduled itself yet is an ordinary state and a defaulted date
          would answer on the department's behalf.
        */}
        <div className="grid gap-4 sm:grid-cols-2">
          <DateField
            id="ppmp-posting-date"
            label="Posting"
            description="When the opportunity is expected to be posted."
            value={postingDate}
            onChange={setPostingDate}
            disabled={saving}
            error={error?.fieldError('postingDate')}
          />

          <DateField
            id="ppmp-opening-of-bids-date"
            label="Opening of bids"
            description="On or after the posting date."
            value={openingOfBidsDate}
            onChange={setOpeningOfBidsDate}
            disabled={saving}
            error={error?.fieldError('openingOfBidsDate')}
          />

          <DateField
            id="ppmp-notice-of-award-date"
            label="Notice of award"
            description="On or after the opening of bids."
            value={noticeOfAwardDate}
            onChange={setNoticeOfAwardDate}
            disabled={saving}
            error={error?.fieldError('noticeOfAwardDate')}
          />

          <DateField
            id="ppmp-contract-signing-date"
            label="Contract signing"
            description="On or after the notice of award."
            value={contractSigningDate}
            onChange={setContractSigningDate}
            disabled={saving}
            error={error?.fieldError('contractSigningDate')}
          />
        </div>

        {!editing ? (
          <p className="text-xs text-slate-500">
            The PPMP is filed EMPTY — its procurement lines are added from its own profile, so a
            later correction to this form cannot disturb them. The number is assigned on save as{' '}
            <span className="font-mono">PPMP-{plan.fiscalYear}-NNNN</span>.
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
